import EditorialHero from '../../editorial-hero';
import JumpLinkStickyTab from '../../jump-link-sticky-tab';
import type { JumpLinkSection } from '../../jump-link-sticky-tab';
import EditorialOverview from '../../editorial-overview';
import FullBleedItinerary from '../../full-bleed-itinerary';
import { FeaturedProfileCard } from '../../featured-profile-card';
import ExperienceDetailsList from '../../experience-details-list';
import { useTealium } from '@fshr/context/tealium-context';

import type { BreadcrumbItem } from '../../breadcrumb';
import type { SignatureProductDetailData } from './types';
import type { PdpSection, ProductDetailStores } from '../shared/types';

const SECTION_IDS = {
	hero: 'signature-pdp-hero',
	jumpNav: 'signature-pdp-jump-nav',
	overview: 'signature-pdp-overview',
	itinerary: 'signature-pdp-itinerary',
	experienceDetails: 'signature-pdp-experience-details',
	host: 'signature-pdp-meet-your-host',
	youMayAlsoLike: 'signature-pdp-you-may-also-like',
} as const;

const FULL_BLEED_ITINERARY_MIN_STEPS = 3;

const SUB_NAV_INTERACTION_DETAILS: Record<string, string> = {
	overview: 'overview',
	itinerary: 'itinerary',
	'experience-details': 'experience details',
	host: 'meet your host',
};

function ProductDetailSubNavigation({ sections }: { sections: JumpLinkSection[] }) {
	const { trackInteraction } = useTealium();

	return (
		<JumpLinkStickyTab
			sections={sections}
			onSectionClick={section => {
				const interactionDetail = SUB_NAV_INTERACTION_DETAILS[section.key];
				if (interactionDetail) {
					trackInteraction('product detail page', 'sub navigation', interactionDetail);
				}
			}}
		/>
	);
}

export function signatureSections(
	product: SignatureProductDetailData,
	stores: ProductDetailStores,
	breadcrumb?: BreadcrumbItem[]
): PdpSection[] {
	const { hero, overview, itinerary, experienceDetails, host } = product;
	const { userStore, productAvailabilityStore } = stores;

	const body: PdpSection[] = (
		[
			overview && {
				key: 'overview',
				id: SECTION_IDS.overview,
				label: 'Overview',
				render: () => <EditorialOverview {...overview} />,
			},

			!!itinerary &&
				(itinerary.steps?.length ?? 0) >= FULL_BLEED_ITINERARY_MIN_STEPS && {
					key: 'itinerary',
					id: SECTION_IDS.itinerary,
					label: 'Itinerary',
					render: () => <FullBleedItinerary steps={itinerary.steps} title={itinerary.title} />,
				},

			experienceDetails && {
				key: 'experience-details',
				id: SECTION_IDS.experienceDetails,
				label: 'Experience Details',
				layout: 'beside-aside',
				render: () => (
					<ExperienceDetailsList
						{...experienceDetails}
						userStore={userStore}
						productId={product.id}
						productAvailabilityStore={productAvailabilityStore}
					/>
				),
			},

			host && {
				key: 'host',
				id: SECTION_IDS.host,
				label: 'Meet Your Host',
				layout: 'beside-aside',
				render: () => <FeaturedProfileCard {...host} />,
			},
		] as (PdpSection | false | undefined)[]
	).filter((section): section is PdpSection => Boolean(section));

	const navEntries: JumpLinkSection[] = body
		.filter(section => section.label)
		.map(section => ({ key: section.key, id: section.id, label: section.label as string }));

	const sections: (PdpSection | false | undefined)[] = [
		hero && {
			key: 'hero',
			id: SECTION_IDS.hero,
			render: () => <EditorialHero {...hero} breadcrumb={breadcrumb} />,
		},

		navEntries.length > 0 && {
			key: 'jump-nav',
			id: SECTION_IDS.jumpNav,
			render: () => <ProductDetailSubNavigation sections={navEntries} />,
		},

		...body,
	];

	return sections.filter((section): section is PdpSection => Boolean(section));
}
